<template>
  <div class="surface-50 py-5 px-3 md:px-5">
    <Card
      class="w-full md:w-10 lg:w-8 mx-auto shadow-1 overflow-hidden"
      :pt="courseCardPassThrough"
    >
      <template #content>
        <article>
          <header class="flex flex-column sm:flex-row align-items-start gap-3 md:gap-4">
            <Avatar
              icon="pi pi-book"
              size="xlarge"
              shape="circle"
              class="bg-primary text-white flex-shrink-0"
              :pt="{ icon: { class: 'text-2xl' } }"
            />

            <div class="flex-1 min-w-0">
              <div class="text-sm font-semibold text-primary mb-1">Course details</div>
              <h1 class="text-2xl md:text-4xl line-height-2 text-900 mt-0 mb-3">
                {{ item.name }}
              </h1>

              <div class="flex flex-wrap gap-2">
                <Tag
                  v-if="item.code"
                  :value="`Code: ${item.code}`"
                  rounded
                  :pt="primaryTagPassThrough"
                />
                <Tag
                  v-if="subjectCategory?.name"
                  :value="subjectCategory.name"
                  rounded
                  :pt="secondaryTagPassThrough"
                />
                <Tag
                  v-if="!isMicroSeminaryOnly && ![null, undefined].includes(item.credits)"
                  :value="`${item.credits} credits`"
                  rounded
                  :pt="secondaryTagPassThrough"
                />
              </div>
            </div>
          </header>

          <template v-if="scheduleDetails.length">
            <Divider class="my-4 md:my-5" />
            <section aria-labelledby="course-schedule-heading">
              <h2 id="course-schedule-heading" class="text-xl text-900 mt-0 mb-2">Schedule</h2>

              <dl class="grid mt-0 mb-0">
                <div v-for="detail in scheduleDetails" :key="detail.label" class="col-12 md:col-6">
                  <div
                    class="surface-50 border-1 border-100 border-round p-3 h-full flex align-items-start gap-3"
                  >
                    <Avatar
                      :icon="detail.icon"
                      shape="circle"
                      class="surface-100 text-primary flex-shrink-0"
                    />
                    <div>
                      <dt class="text-sm font-semibold text-600 mb-1">{{ detail.label }}</dt>
                      <dd class="text-900 font-medium m-0">{{ detail.value }}</dd>
                    </div>
                  </div>
                </div>
              </dl>
            </section>
          </template>

          <template v-if="programmes.length || prerequisites.length">
            <Divider class="my-4 md:my-5" />
            <section aria-labelledby="course-pathways-heading">
              <h2 id="course-pathways-heading" class="text-xl text-900 mt-0 mb-2">
                Study information
              </h2>

              <div class="grid">
                <div
                  v-if="programmes.length"
                  :class="['col-12', { 'md:col-6': hasBothRelationships }]"
                >
                  <Card
                    class="surface-50 border-1 border-100 shadow-none h-full"
                    :pt="relatedCardPassThrough"
                  >
                    <template #title>
                      <h3 class="text-base text-900 m-0 flex align-items-center gap-2">
                        <i class="pi pi-graduation-cap text-primary" aria-hidden="true"></i>
                        Programmes
                      </h3>
                    </template>
                    <template #content>
                      <ul class="list-none p-0 m-0 flex flex-column gap-2">
                        <li v-for="programme in programmes" :key="programme.id">
                          <NuxtLink
                            :to="programme.link"
                            class="course-link flex align-items-center gap-2 text-sm text-primary hover:underline"
                          >
                            <i class="pi pi-book text-xs" aria-hidden="true"></i>
                            <span>{{ programme.name }}</span>
                          </NuxtLink>
                        </li>
                      </ul>
                    </template>
                  </Card>
                </div>

                <div
                  v-if="prerequisites.length"
                  :class="['col-12', { 'md:col-6': hasBothRelationships }]"
                >
                  <Card
                    class="surface-50 border-1 border-100 shadow-none h-full"
                    :pt="relatedCardPassThrough"
                  >
                    <template #title>
                      <h3 class="text-base text-900 m-0 flex align-items-center gap-2">
                        <i class="pi pi-check-circle text-primary" aria-hidden="true"></i>
                        Prerequisites
                      </h3>
                    </template>
                    <template #content>
                      <ul class="list-none p-0 m-0 flex flex-column gap-2">
                        <li v-for="subject in prerequisites" :key="subject.id">
                          <NuxtLink
                            :to="subject.link"
                            class="course-link flex align-items-center gap-2 text-sm text-primary hover:underline"
                          >
                            <i class="pi pi-book text-xs" aria-hidden="true"></i>
                            <span>{{ subject.name }}</span>
                          </NuxtLink>
                        </li>
                      </ul>
                    </template>
                  </Card>
                </div>
              </div>
            </section>
          </template>

          <template v-if="hasOverview">
            <Divider class="my-4 md:my-5" />
            <section aria-labelledby="course-overview-heading">
              <h2 id="course-overview-heading" class="text-xl text-900 mt-0 mb-3">
                About this course
              </h2>
              <div
                v-if="item.description"
                class="course-description text-sm md:text-base text-700 line-height-3"
                v-html="item.description"
              />
              <p v-else class="text-sm md:text-base text-700 line-height-3 m-0">
                {{ item.summary }}
              </p>
            </section>
          </template>
        </article>
      </template>
    </Card>
  </div>
</template>

<script setup>
const MICRO_SEMINARY_PROGRAMME = {
  id: 27,
  name: 'Micro Seminary',
  link: '/micro-seminary',
}

const props = defineProps({
  item: {
    type: Object,
    required: true,
  },
})

const programmeIDs = Array.isArray(props.item.programme_ids) ? props.item.programme_ids : []
const prerequisiteIDs = Array.isArray(props.item.prerequisite_ids)
  ? props.item.prerequisite_ids
  : []

const courseCardPassThrough = {
  body: { class: 'p-3 md:p-5' },
  content: { class: 'p-0' },
}

const relatedCardPassThrough = {
  body: { class: 'p-3 md:p-4 h-full' },
  caption: { class: 'm-0' },
  title: { class: 'm-0' },
  content: { class: 'pt-3 pb-0' },
}

const primaryTagPassThrough = {
  root: {
    class: 'bg-primary-50 text-primary border-none px-3 py-2',
  },
}

const secondaryTagPassThrough = {
  root: { class: 'surface-100 text-700 border-none px-3 py-2' },
}

const subjectCategoryRequest = useAsyncData(
  `course-${props.item.id}-subject-category`,
  () => {
    if (!props.item.subject_category_id) return {}

    return queryContent('subject_category')
      .where({
        _path: { $regex: new RegExp('^\\/subject_category\\/.*') },
        id: props.item.subject_category_id,
      })
      .only(['id', 'name'])
      .findOne()
  },
  { default: () => ({}) }
)

const programmeResultsRequest = useAsyncData(
  `course-${props.item.id}-programmes`,
  () =>
    programmeIDs.length
      ? queryContent('programmes')
          .where({
            _path: { $regex: new RegExp('^\\/programmes\\/.*') },
            id: { $in: programmeIDs },
          })
          .only(['id', 'name', 'link'])
          .find()
      : [],
  { default: () => [] }
)

const prerequisiteResultsRequest = useAsyncData(
  `course-${props.item.id}-prerequisites`,
  () =>
    prerequisiteIDs.length
      ? queryContent('resources/courses')
          .where({
            _path: { $regex: new RegExp('^\\/resources\\/courses\\/.*') },
            id: { $in: prerequisiteIDs },
          })
          .only(['id', 'name', 'link'])
          .find()
      : [],
  { default: () => [] }
)

const [{ data: subjectCategory }, { data: programmeResults }, { data: prerequisiteResults }] =
  await Promise.all([subjectCategoryRequest, programmeResultsRequest, prerequisiteResultsRequest])

const programmes = computed(() =>
  sortBySourceOrder([...(programmeResults.value || []), MICRO_SEMINARY_PROGRAMME], programmeIDs)
)
const isMicroSeminaryOnly = computed(
  () =>
    programmes.value.length > 0 &&
    programmes.value.every(
      (programme) => String(programme.id) === String(MICRO_SEMINARY_PROGRAMME.id)
    )
)
const prerequisites = computed(() => sortBySourceOrder(prerequisiteResults.value, prerequisiteIDs))
const hasBothRelationships = computed(() => programmes.value.length && prerequisites.value.length)
const hasOverview = computed(() =>
  Boolean(props.item.description?.trim() || props.item.summary?.trim())
)

const scheduleDetails = computed(() =>
  [
    {
      label: 'Course duration',
      icon: 'pi pi-calendar',
      value: formatDateRange(props.item.effective_start_date, props.item.effective_end_date),
    },
    {
      label: 'Enrolment period',
      icon: 'pi pi-clock',
      value: formatDateRange(props.item.enrolment_start_date, props.item.enrolment_end_date),
    },
  ].filter((detail) => detail.value)
)

const dateFormatter = new Intl.DateTimeFormat('en-ZA', {
  day: 'numeric',
  month: 'short',
  year: 'numeric',
  timeZone: 'UTC',
})

function sortBySourceOrder(items, ids) {
  const itemMap = new Map((items || []).map((item) => [String(item.id), item]))
  return ids.map((id) => itemMap.get(String(id))).filter(Boolean)
}

function formatDate(value) {
  if (!value) return ''

  const date = new Date(value.length === 10 ? `${value}T00:00:00Z` : value)
  return Number.isNaN(date.getTime()) ? value : dateFormatter.format(date)
}

function formatDateRange(startDate, endDate) {
  const start = formatDate(startDate)
  const end = formatDate(endDate)

  if (start && end) return `${start} – ${end}`
  if (start) return `From ${start}`
  if (end) return `Until ${end}`
  return ''
}
</script>

<style scoped>
.course-link {
  text-decoration: none;
}

.course-link:focus-visible {
  text-decoration: underline;
}

.course-description :deep(> :first-child) {
  margin-top: 0;
}

.course-description :deep(> :last-child) {
  margin-bottom: 0;
}

.course-description :deep(li + li) {
  margin-top: 0.5rem;
}
</style>
